import * as React from 'react';
import { DevicesProvider, useEditor, useEditorMaybe, WithEditor } from '@grapesjs/react';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { TextInput, View, Platform } from 'react-native';
import ToolSetButtons from './ToolSetButtons';
import { LinearGradient } from 'expo-linear-gradient';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import TopbarButtons from './TopBarButtons';
import { FontAwesome6 } from '@expo/vector-icons';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';

// Styled components for enhanced theming
const StyledTopbar = styled(Box)(({ theme }) => ({
  height: 70,
  gap: 30,
  marginHorizontal: -50,
  paddingRight: 100,
  paddingHorizontal: 80,
  padding: 16,
  borderBottomLeftRadius: 40,
  borderBottomRightRadius: 40,
  shadowRadius: 20,
  shadowOpacity: 0.3,
  paddingVertical: 16,
  flexDirection: "row",
  alignItems: 'center',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
}));

const StyledTextInput = styled(TextInput)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: 24,
  flex: 1,
  alignSelf: 'stretch',
  color: 'white',
  borderWidth: 0,
  borderBottomWidth: 2,
  borderBottomColor: 'rgba(255,255,255,0.5)',
  backgroundColor: 'transparent',
  padding: 8,
  borderRadius: 8,
  '&:focus': {
    borderBottomColor: 'white',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: theme?.palette?.mode === 'dark' ? '#2d2d2d' : 'white',
  color: theme?.palette?.mode === 'dark' ? '#ffffff' : '#333333',
  borderRadius: 12,
  minHeight: 40,
  fontSize: 14,
  fontWeight: 500,
  boxShadow: theme?.palette?.mode === 'dark' 
    ? '0 4px 12px rgba(0,0,0,0.3)' 
    : '0 4px 12px rgba(0,0,0,0.1)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme?.palette?.mode === 'dark' ? '#4a90e2' : '#e0e0e0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme?.palette?.mode === 'dark' ? '#5ba0f2' : '#4a90e2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme?.palette?.mode === 'dark' ? '#6bb6ff' : '#4a90e2',
    borderWidth: 2,
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.2)',
  color: 'white',
  padding: 12,
  borderRadius: 12,
  backdropFilter: 'blur(5px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.3)',
    transform: 'scale(1.05)',
  },
}));

const Topbar = ({ type, projectData, saveCallback, duplicateCallback }) => {
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState(projectData?.description);
  const theme = themeSelector();
  const editor = useEditorMaybe();

  React.useEffect(() => {
    if (projectData) {
      setTitle(projectData.title);
      setDescription(projectData.description);
    }
  }, [projectData])

  return (
    <Animatable.View useNativeDriver animation={'slideInDown'} style={{ flex: 1 }}>
      <LinearGradient 
        colors={theme ? ['#667eea', '#764ba2'] : ['#2c3e50', '#34495e']} 
        style={{ 
          height: 70, 
          gap: 30, 
          marginHorizontal: -50, 
          paddingRight: 100, 
          paddingHorizontal: 80, 
          padding: 16, 
          borderBottomLeftRadius: 40, 
          borderBottomRightRadius: 40,
          shadowRadius: 20, 
          shadowOpacity: 0.3, 
          paddingVertical: 16, 
          flexDirection: "row",
          alignItems: 'center',
        }}
      >
        <View style={[styles.centerEverything, { gap: 30, flexDirection: 'row', flex: 1 }]}>
          <Tooltip title="Save Template" arrow>
            <ActionButton onClick={() => saveCallback?.(title, editor)}>
              <FontAwesome6 name="save" size={20} color="white" />
            </ActionButton>
          </Tooltip>
          
          <View style={{ flex: 1 }}>
            <StyledTextInput
              value={title||""}
              onChangeText={setTitle}
              style={[styles.inputOutline, { 
                fontWeight: "bold", 
                fontSize: 24, 
                minWidth: undefined, 
                flex: 1, 
                alignSelf: 'stretch', 
                color: 'white', 
                borderWidth: 0, 
                borderBottomWidth: 2,
                borderBottomColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'transparent',
                padding: 8,
              }]}
              placeholderTextColor={'rgba(255,255,255,0.7)'}
              placeholder="Template Title"
            />
          </View>
          
          <View style={{ flex: 1 }}>
            <WithEditor>
              <TopbarButtons type={type} />
            </WithEditor>
          </View>
        </View>
        
        <View style={{ flex: 1, maxWidth: 250 }}>
          <DevicesProvider>
            {({ selected, select, devices }) => (
              <FormControl size="small" fullWidth>
                <StyledSelect 
                  value={selected} 
                  onChange={(ev) => select((ev.target as HTMLSelectElement).value)}
                  displayEmpty
                >
                  {devices.map((device) => (
                    <MenuItem 
                      value={device.id} 
                      key={device.id}
                      sx={{
                        backgroundColor: theme ? '#f8f9fa' : '#2d2d2d',
                        color: theme ? '#333333' : '#ffffff',
                        '&:hover': {
                          backgroundColor: theme ? '#e9ecef' : '#3d3d3d',
                        }
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {device.getName()}
                      </Typography>
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            )}
          </DevicesProvider>
        </View>
      </LinearGradient>
    </Animatable.View>
  );
}

export default Topbar;